<template>

    <div class="flex flex-wrap items-end gap-4 mb-5">
        <div class="flex flex-col">
            <label for="orders_date_from" class="text-sm font-medium text-gray-700">From</label>
            <input id="orders_date_from" type="date" v-model="dateFrom" class="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
        </div>

        <div class="flex flex-col">
            <label for="orders_date_to" class="text-sm font-medium text-gray-700">To</label>
            <input id="orders_date_to" type="date" v-model="dateTo" class="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
        </div>

        <button v-if="dateFrom || dateTo" type="button" @click="clearDateFilter" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Clear
        </button>
    </div>

    <div class="flex items-center space-x-2 mb-5" v-if="selectedLeads.length">
        <div class="relative inline-block text-left">
            <div>
                <button @click="showDropdown = !showDropdown" type="button" class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"  aria-expanded="true" aria-haspopup="true">
                    Mark Leads
                    <svg class="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                    </svg>
                </button>
            </div>

            <div v-if="showDropdown" class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                <div class="py-1" role="none">
                    <span @click="markLead('won')" class="text-green-500 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Won</span>
                    <span @click="markLead('lost')" class="text-red-500 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-300" role="menuitem" tabindex="-1">Lost</span>
                    <span @click="markLead('incorrect_details')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Incorrect Details</span>
                    <span @click="markLead('out_of_area')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Out of Area</span>
                    <span @click="markLead('quote_sent')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Quote Sent</span>
                    <span @click="markLead('spam')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Spam</span>
                    <span @click="markLead('unable_to_contact')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Unable to Contact</span>
                    <span @click="markLead('waiting_on_customer')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Waiting on Customer</span>
                </div>
            </div>
        </div>
    </div>

    <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">

        <table class="min-w-full divide-y divide-gray-300">
            <thead class="bg-gray-50">
            <tr>
                <th scope="col" class="relative px-7 sm:w-12 sm:px-6">
                    <input type="checkbox" v-model="selectAll" :value="!selectAll" :checked="selectAll" class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600">
                </th>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Customer</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Phone</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Assigned To</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
            <tr :class="{'bg-gray-50': isLeadSelected(lead.id)}" v-for="(lead, index) in leads_data">
                <td class="relative px-7 sm:w-12 sm:px-6">
                    <!-- Selected row marker, only show when row is selected. -->

                    <div v-if="isLeadSelected(lead.id)" class="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"></div>
                    <input type="checkbox" v-model="selectedLeads" :value="lead.id" :checked="isLeadSelected(lead.id)" class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600">
                </td>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 hover:text-blue-500" :class="{'text-indigo-600': isLeadSelected(lead.id)}">
                    <a :href="'/leads/'+lead.lead.reference">{{ lead.lead.customer.name }}</a>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ lead.lead.customer.email }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ lead.lead.customer.phone }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ lead.name }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                    <ul v-if="lead.assigned_to.length">
                        <li v-for="(user, index) in lead.assigned_to">{{ user }}</li>
                    </ul>
                    <span v-else>-</span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm capitalize text-gray-500"
                    :class="{'text-green-500': (lead.status === 'closed' || lead.status === 'won'), 'text-blue-500': lead.status === 'open', 'text-red-600': lead.status === 'lost'}"
                >
                    {{ lead.status.replaceAll('_', ' ') }}
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ lead.lead?.date }}</td>
            </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
export default {
    props: ['leads'],
    data() {
        return {
            dateFrom: '',
            dateTo: '',
            selectedLeads: [],
            selectAll: false,
            allLeads: this.leads,
            leads_data: this.leads,
            showDropdown: false,
        }
    },
    watch: {
        dateFrom() {
            this.applyDateFilter();
        },
        dateTo() {
            this.applyDateFilter();
        },
        selectAll() {
            if (this.selectAll) {
                this.selectedLeads = this.leads_data.map(lead => lead.id);
            } else {
                this.selectedLeads = [];
            }
        }
    },
    computed: {
        // isLeadSelected() {
        //     return function(id) {
        //         return this.selectedLeads.includes(id)
        //     }
        // }
    },
    methods: {
        applyDateFilter() {
            const from = this.dateFrom ? new Date(`${this.dateFrom}T00:00:00`) : null;
            const to = this.dateTo ? new Date(`${this.dateTo}T23:59:59.999`) : null;

            if (!from && !to) {
                this.leads_data = this.allLeads;
                this.pruneSelectionsToDisplayedRows();
                return;
            }

            this.leads_data = this.allLeads.filter((row) => {
                const raw = row?.lead?.created_at || row?.created_at;
                if (!raw) return true;

                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return true;

                if (from && d < from) return false;
                if (to && d > to) return false;
                return true;
            });

            this.pruneSelectionsToDisplayedRows();
        },
        clearDateFilter() {
            this.dateFrom = '';
            this.dateTo = '';
            this.applyDateFilter();
        },
        pruneSelectionsToDisplayedRows() {
            const allowed = new Set(this.leads_data.map((row) => row.id));
            this.selectedLeads = this.selectedLeads.filter((id) => allowed.has(id));
            if (this.selectAll && this.selectedLeads.length !== this.leads_data.length) {
                this.selectAll = false;
            }
        },
        fetch() {
            axios.get('/leads/fetch').then(response => {
                if (response.data.leads) {
                    this.leads_data = response.data.leads;
                }
            })
        },
        submit() {
            axios.patch('/leads', {
                bulk_close: true,
                selected_leads: this.selectedLeads,
            }).then(response => {
                this.fetch();
                this.selectedLeads = [];
            }).catch(error => {
                //
            })
        },
        markLead(status) {
            axios.patch('/leads', {
                bulk_close: true,
                status: status,
                close_lead: 'true',
                selected_leads: this.selectedLeads,
            }).then(response => {
                window.location.reload();
            })
        },
        isLeadSelected(id) {
            return this.selectedLeads.includes(id)
        }
    }
}
</script>
